import arrowLeftIcon from "@tabler/icons/outline/arrow-left.svg";
import xIcon from "@tabler/icons/outline/x.svg";
import React from "react";
import { defineMessages, useIntl } from "react-intl";

import { cn } from "@/utils/cn";

import Button, { type ButtonVariant } from "../button/button";
import HStack from "../hstack/hstack";
import IconButton from "../icon-button/icon-button";
import Stack from "../stack/stack";
import Text, { type Sizes } from "../text/text";

const messages = defineMessages({
  back: { id: "navigation_bar.back", defaultMessage: "Back" },
  close: { id: "lightbox.close", defaultMessage: "Close" },
  confirm: { id: "confirmations.delete.confirm", defaultMessage: "Delete" },
});

const widths = {
  xs: "max-w-xs",
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
};

export interface ModalProps {
  /** Optionally stack the confirmation / secondary buttons */
  isActionButtonsStacked?: boolean;
  /** Callback when the modal is cancelled. */
  cancelAction?: () => void;
  /** Cancel button text. */
  cancelText?: React.ReactNode;
  /** Cancel button theme. */
  cancelTheme?: ButtonVariant;
  /** URL to an SVG icon for the close button. */
  closeIcon?: string;
  /** Callback when the modal is confirmed. */
  confirmationAction?: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  /** Whether the confirmation button is disabled. */
  confirmationDisabled?: boolean;
  /** Confirmation button text. */
  confirmationText?: React.ReactNode;
  /** Confirmation button theme. */
  confirmationTheme?: ButtonVariant;
  /** Whether to use full width style for confirmation button. */
  confirmationFullWidth?: boolean;
  /** Callback when back button is clicked. */
  onBackClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Callback when the modal is closed. */
  onClose?: () => void;
  /** Callback when the secondary action is chosen. */
  secondaryAction?: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  /** Secondary button text. */
  secondaryText?: React.ReactNode;
  secondaryDisabled?: boolean;
  /** Secondary button theme. */
  secondaryTheme?: ButtonVariant;
  /** Don't focus the "confirm" button on mount. */
  skipFocus?: boolean;
  /** Subtitle text for the modal */
  subtitle?: React.ReactNode;
  /** Title text for the modal. */
  title?: React.ReactNode;
  titleSize?: Sizes;
  width?: keyof typeof widths;
  children?: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  /** Back arrow display */
  withBackButton?: boolean;
  /** Display close button with no title */
  withStandaloneCloseButton?: boolean;
  /** Optional slot for action on header's right side */
  headerAction?: React.ReactNode;
  ref?: React.Ref<HTMLDivElement>;
}

/** Displays a modal dialog box. */
const Modal = ({
  bodyClassName,
  cancelAction,
  cancelText,
  cancelTheme,
  children,
  className,
  closeIcon = xIcon,
  confirmationAction,
  confirmationDisabled,
  confirmationText,
  confirmationTheme,
  confirmationFullWidth,
  isActionButtonsStacked = false,
  onBackClick,
  onClose,
  secondaryAction,
  secondaryDisabled = false,
  secondaryText,
  secondaryTheme,
  skipFocus = false,
  subtitle,
  title,
  titleSize = "lg",
  width = "xl",
  withBackButton = false,
  withStandaloneCloseButton = false,
  headerAction,
  ref,
}: ModalProps) => {
  const intl = useIntl();
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const hasLargeTitle = titleSize === "3xl";

  const handleKeyUp = (e: KeyboardEvent) => {
    if (e.key === "Escape" || e.key === "Esc" || e.keyCode === 27) {
      if (onClose) onClose();
    }
  };

  const ActionContainer = isActionButtonsStacked ? Stack : HStack;

  React.useEffect(() => {
    if (buttonRef?.current && !skipFocus) {
      buttonRef.current.focus();
    }
  }, [skipFocus, buttonRef]);

  React.useEffect(() => {
    window.addEventListener("keyup", handleKeyUp, false);

    return () => {
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [children]);

  const shouldRenderHeader = title || withStandaloneCloseButton || headerAction;

  return (
    <div
      role="dialog"
      className="fixed inset-0 z-1002 mx-auto flex min-h-full items-end justify-center p-4 sm:my-2 sm:min-h-[calc(100%-3.5rem)] sm:items-center sm:p-0"
      data-testid="modal-component"
      onClick={(e) => {
        if (e.target === e.currentTarget && onClose) {
          if (onClose) onClose();
        }
      }}
    >
      <div
        ref={ref}
        className={cn(
          className,
          "pointer-events-auto mx-auto block max-h-full w-full overflow-auto rounded-2xl bg-white text-start align-middle text-gray-900 shadow-xl transition-all dark:bg-gray-900 dark:text-gray-100",
          widths[width],
        )}
      >
        <div className="w-full justify-between sm:flex sm:items-start">
          <div className="w-full">
            {shouldRenderHeader && (
              <HStack
                alignItems={subtitle || hasLargeTitle ? "start" : "center"}
                space={hasLargeTitle ? 0 : 2}
                justifyContent="between"
                className={cn("px-6 pt-6", {
                  "flex-col": hasLargeTitle,
                })}
              >
                {withBackButton ? (
                  <IconButton
                    src={arrowLeftIcon}
                    title={intl.formatMessage(messages.back)}
                    onClick={onBackClick}
                    iconClassName="size-6 text-gray-900 dark:text-white"
                    className="-ml-2"
                  />
                ) : (
                  <>
                    {onClose && (
                      <IconButton
                        src={closeIcon}
                        title={intl.formatMessage(messages.close)}
                        onClick={onClose}
                        className="-ml-2 text-gray-900 rtl:rotate-180 dark:text-white"
                        data-testid="close-modal"
                      />
                    )}
                  </>
                )}

                {title && (
                  <Stack
                    space={1}
                    grow
                    className={cn({
                      "pt-4": hasLargeTitle,
                    })}
                  >
                    <Text
                      tag={hasLargeTitle ? "h1" : "h3"}
                      size={titleSize}
                      weight="bold"
                      className="grow"
                      data-testid="modal-title"
                    >
                      {title}
                    </Text>

                    {subtitle && (
                      <Text
                        theme="muted"
                        className="text-wrap"
                        size={hasLargeTitle ? "xmd" : "md"}
                      >
                        {subtitle}
                      </Text>
                    )}
                  </Stack>
                )}

                {headerAction && headerAction}
              </HStack>
            )}

            {shouldRenderHeader ? (
              <div
                className={cn(bodyClassName, "mt-4 w-full px-6", {
                  "pb-6": !confirmationAction,
                })}
              >
                {children}
              </div>
            ) : (
              children
            )}
          </div>
        </div>

        {confirmationAction && (
          <HStack
            className="mt-5 px-6 pb-6"
            justifyContent="between"
            data-testid="modal-actions"
          >
            <div className={cn({ grow: !confirmationFullWidth })}>
              {cancelAction && (
                <Button
                  variant={cancelTheme || "outline"}
                  onClick={cancelAction}
                >
                  {cancelText || "Cancel"}
                </Button>
              )}
            </div>

            <ActionContainer
              space={2}
              className={cn({ grow: confirmationFullWidth })}
            >
              {typeof secondaryAction !== "undefined" && (
                <Button
                  variant={secondaryTheme || "secondary"}
                  onClick={secondaryAction}
                  disabled={secondaryDisabled}
                  className={isActionButtonsStacked ? "order-2 mt-2" : ""}
                  data-testid="secondary-button"
                >
                  {secondaryText}
                </Button>
              )}

              <Button
                variant={confirmationTheme || "default"}
                onClick={confirmationAction}
                disabled={confirmationDisabled}
                ref={buttonRef}
                block={confirmationFullWidth}
              >
                {confirmationText}
              </Button>
            </ActionContainer>
          </HStack>
        )}
      </div>
    </div>
  );
};

export { Modal as default, Modal };
